import { useCallback, useEffect, useState } from 'react';
import { performPostRequestWithRetry } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import {
	editionToEditionKey,
	type ApplicationEdition,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { hasStatusCode, isServerSideResponse } from '../common/http-error-checks.tsx';

type UseTargetEditionProps = {
	sourceProduct: string;
	targetProduct: string;
};

type EditionResponse = {
	edition: string;
	strategy: string;
};

export const editionKeyToEdition = (edition?: string): ApplicationEdition => {
	switch (edition) {
		case 'free':
			return 'FREE_EDITION';
		case 'standard':
			return 'STANDARD_EDITION';
		case 'premium':
			return 'PREMIUM_EDITION';
		default:
			return 'UNLICENSED';
	}
};

export const useTargetEdition = ({ sourceProduct, targetProduct }: UseTargetEditionProps) => {
	const cloudId = useCloudId();
	const appEditions = useAppEditions();

	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	const [data, setData] = useState<EditionResponse | null>(null);

	const getSourceEdition = useCallback((): string | undefined => {
		let sourceEdition: ApplicationEdition = 'FREE_EDITION';
		switch (sourceProduct) {
			case 'jira-software.ondemand':
				sourceEdition = appEditions?.software;
				break;
			case 'jira-service-desk.ondemand':
				sourceEdition = appEditions?.serviceDesk;
				break;
			case 'jira-core.ondemand':
				sourceEdition = appEditions?.core;
				break;
			case 'jira-product-discovery':
				sourceEdition = appEditions?.productDiscovery;
				break;
			default:
				sourceEdition = 'FREE_EDITION';
		}
		return editionToEditionKey(sourceEdition);
	}, [appEditions, sourceProduct]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);

			try {
				const targetEdition = await performPostRequestWithRetry<EditionResponse>(
					`/gateway/api/xflow/${cloudId}/${targetProduct}/edition`,
					{
						body: JSON.stringify({
							requestedEditionId: getSourceEdition() || 'FREE_EDITION',
						}),
						retryPredicate: (err: Error) => {
							const errorCode = hasStatusCode(err) ? err.statusCode : undefined;
							return isServerSideResponse(errorCode);
						},
						retryAttempts: 3,
					},
				);

				setData(targetEdition);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setError(err);
			}
			setLoading(false);
		};

		fetchData();
	}, [cloudId, targetProduct, getSourceEdition]);

	return {
		loading,
		error,
		data,
	};
};
